import { IconCalendarWeek, IconChevronDown, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import clsx from "clsx";
import { getMonth, getYear } from "date-fns";
import { range } from "lodash";
import { useState } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button } from "@/components/core/buttons/button";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import moment from "moment-timezone";
import { ScrollArea } from "../scroller";

const TIMEZONE = "Asia/Manila";

type DatePickerProps = {
	onChange: (date: Date) => void;
	currentDate?: Date;
	id: string;
	className?: string;
	showIcon?: boolean;
	showTimePicker?: boolean;
	placeholder?: string | undefined;
};

export default function FormCalendar({ onChange, currentDate, className, showIcon = false, showTimePicker = false, placeholder }: DatePickerProps) {
	const [startDate, setStartDate] = useState(currentDate);
	const [openYear, setOpenYear] = useState(false);
	const [openMonth, setOpenMonth] = useState(false);

	const years = range(1950, getYear(new Date()) + 1, 1);
	const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

	const handleDateChange = (date: Date) => {
		if (date) {
			const updatedDate = moment(date).tz(TIMEZONE).toDate();
			setStartDate(updatedDate);
			onChange(updatedDate);
		}
	};

	return (
		<div className="relative w-full">
			<ReactDatePicker
				autoComplete="off"
				selected={startDate}
				onChange={handleDateChange}
				dateFormat={showTimePicker ? "yyyy/MM/dd hh:mm aa" : "yyyy/MM/dd"}
				showTimeSelect={showTimePicker}
				timeFormat="hh:mm aa"
				placeholderText={placeholder}
				timeIntervals={15}
				className={clsx(
					"w-full min-h-[38px] px-3 text-base rounded-md border border-border focus:border-focus-border focus:ring-2 ring-offset-0 focus:ring-focus-ring/30 outline-0 outline-none text-foreground",
					{ "pr-8": showIcon, "pr-3": !showIcon },
					className,
				)}
				wrapperClassName="relative w-full"
				renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth }) => (
					<div className="flex justify-between items-center p-4">
						<button type="button" onClick={decreaseMonth} className="text-foreground/50 hover:text-foreground">
							<IconChevronLeft size={18} stroke={2} />
						</button>

						<div className="inline-flex flex-row items-center w-auto gap-1">
							<Popover open={openMonth} onOpenChange={setOpenMonth}>
								<PopoverTrigger asChild>
									<Button size="sm" variant="ghost" className="text-foreground font-medium flex items-center gap-1">
										{months[getMonth(date)]} <IconChevronDown size={16} stroke={2} />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="z-10 bg-white shadow-lg rounded-md w-auto">
									<ScrollArea className="h-[200px]">
										{months.map((option, index) => (
											<button
												type="button"
												key={option}
												onClick={() => {
													changeMonth(index);
													setOpenMonth(false);
												}}
												className={clsx("flex w-full py-2 px-3 hover:bg-slate-100 text-foreground cursor-pointer text-center", {
													"bg-primary-600/10 hover:bg-primary-600/10 text-primary-600": getMonth(date) === index,
												})}
											>
												{option}
											</button>
										))}
									</ScrollArea>
								</PopoverContent>
							</Popover>

							{/* Year Selector with Radix Popover */}
							<Popover open={openYear} onOpenChange={setOpenYear}>
								<PopoverTrigger asChild>
									<Button size="sm" variant="ghost" className="text-foreground font-medium flex items-center gap-1">
										{getYear(date)} <IconChevronDown size={16} stroke={2} />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="z-10 bg-white shadow-lg rounded-md w-auto">
									<ScrollArea className="h-[200px]">
										{years.map((option) => (
											<button
												type="button"
												key={option}
												onClick={() => {
													changeYear(option);
													setOpenYear(false);
												}}
												className={clsx("flex w-full py-2 pl-3 pr-4 hover:bg-slate-100 text-foreground cursor-pointer text-center", {
													"bg-primary-600/10 hover:bg-primary-600/10 text-primary-600": getYear(date) === option,
												})}
											>
												{option}
											</button>
										))}
									</ScrollArea>
								</PopoverContent>
							</Popover>
						</div>
						<button type="button" onClick={increaseMonth} className="text-foreground/50 hover:text-foreground">
							<IconChevronRight size={18} stroke={2} />
						</button>
					</div>
				)}
			/>

			{showIcon && <IconCalendarWeek className="absolute top-1/2 right-3 transform -translate-y-1/2 text-muted-foreground" size={18} stroke={1.5} />}
		</div>
	);
}
